import { Checkbox, FormControlLabel, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { FormikProps, useFormik } from "formik";
import { useMemo, useState } from "react";
import * as Yup from "yup";
import CustomDatePicker from "../../../../../../../../components/CustomDatePicker";
import CustomSelect from "../../../../../../../../components/CustomSelect";
import CustomTimePicker from "../../../../../../../../components/CustomTimePicker";
import Flex from "../../../../../../../../components/Flex";
import NumberInput from "../../../../../../../../components/NumberInput";
import MaskMultiSelectScroll from "../../../../../../../../components/MaskMultiSelectScroll";
import { MultiSelect } from "../../../../../../../../components/MultiSelect";
import Text from "../../../../../../../../components/Text";
import StyledButtonV2 from "../../../../../../../../components/app/StyledButtonV2";
import { RecurringConfig } from "../../../../../../../../components/app/provider/scheduling/RecurringConfig";
import StyledFormModal from "../../../../../../../../components/globalModal/StyledModal";
import useSelectedPatient from "../../../../../../../../hooks/local-storage/use-selected-patient";
import { useDebounce } from "../../../../../../../../hooks/use-debounce";
import { DisclosureHost } from "../../../../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../../../../hooks/useSnackbar";
import { useGetAllScreenerType } from "../../../../../../../../queries/patient-chart/assessment/assement-forms/use-get-all-screener-type";
import { useSendPatientAssessments } from "../../../../../../../../queries/patient-chart/assessment/assement-forms/use-send-patient-assessments";
import { useUpdateScheduledAssessment } from "../../../../../../../../queries/patient-chart/assessment/assement-forms/use-update-scheduled-assessment";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getXtenantId,
  replaceUnderScoreAddSpace,
  showToastOnApiCall,
} from "../../../../../../../../utils/utils";
import {
  REMINDER_BEFORE_UNITS,
  REMINDER_MEDIUMS,
} from "../../../../../scheduling/appointment/appointment-constant";
import {
  MEDIUM_LIST,
  PatientAssessment,
  SEND_PATIENT_ASSESSMENT_INITIAL_VALUES,
} from "./assessment-constant";
import { useSearchPatientList } from "../../../../../../../../queries/list/useSearchPatientList";
import { uniqBy } from "lodash";
import useCurrentUser from "../../../../../../../../hooks/local-storage/use-current-user";

interface SendPatientAssessmentFormsModalProps {
  disclosureHost: DisclosureHost;
  showPatientSelection?: boolean;
  editMode?: boolean;
  onUpdateSuccess?: () => void;
}

interface EditAssessmentData {
  assessmentId: string;
  patientId: string;
  patientName?: string;
  appointmentId?: string | null;
  screenerType: string;
  scheduleTime: string;
}

interface DisclosureData {
  patientIds?: Array<{ uuid: string; label: string }>;
  editAssessment?: EditAssessmentData;
}

const SEND_PATIENT_ASSESSMENT_VALIDATION_SCHEMA = Yup.object({
  selectedForms: Yup.array()
    .required("Please select at least one form")
    .min(1, "Please select at least one form"),
  medium: Yup.string().optional(),
  isScheduled: Yup.boolean(),
  recordedDate: Yup.date().when("isScheduled", {
    is: (value: boolean) => !!value,
    then: (schema) =>
      schema
        .required("Recorded date is required")
        .test("future-date", "Date should be in the future", (value) => {
          if (!value) return true;
          return !dayjs(value).isBefore(dayjs(), "day");
        }),
    otherwise: (schema) => schema.notRequired(),
  }),
  recordedTime: Yup.string()
    .when("isScheduled", {
      is: (value: boolean) => !!value,
      then: (schema) => schema.required("Recorded Time is required"),
      otherwise: (schema) => schema.notRequired(),
    })
    .test("time", "Time should be in the future", function (value) {
      const recordedDate = this.parent?.recordedDate;
      if (!recordedDate || !value) return true;
      if (!dayjs(recordedDate).isSame(dayjs(), "day")) {
        return true;
      }
      const merged = dayjs(recordedDate)
        .hour(dayjs(value).hour())
        .minute(dayjs(value).minute())
        .second(0);
      return merged.isAfter(dayjs());
    }),
});

const SendPatientAssessmentFormsModal = ({
  showPatientSelection = false,
  disclosureHost,
  editMode = false,
  onUpdateSuccess,
}: SendPatientAssessmentFormsModalProps) => {
  const showSnackbar = useSnackbar();
  const [selectedPatient] = useSelectedPatient();
  const queryClient = useQueryClient();
  const [me] = useCurrentUser();
  const providerName = me?.firstName + " " + me?.lastName;
  const providerRole = me?.role;
  const disclosureData = disclosureHost.data as DisclosureData | undefined;
  const preSelectedPatients = disclosureData?.patientIds;

  // Edit context: only consider edit mode valid when both the editMode flag is
  // set AND we received an assessment UUID. Memoized so identity is stable
  // across renders (Formik's enableReinitialize relies on this).
  const editAssessment = useMemo<EditAssessmentData | undefined>(() => {
    if (!editMode) return undefined;
    const candidate = disclosureData?.editAssessment;
    if (!candidate?.assessmentId) return undefined;
    return candidate;
  }, [editMode, disclosureData?.editAssessment]);

  const isEdit = !!editAssessment;
  const [searchString, setSearchString] = useDebounce<string>("");
  const [screenerPagination, setScreenerPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: screenerTypeList, isFetching: isScreenerFetching } =
    useGetAllScreenerType({
      pagination: screenerPagination,
      xTENANTID: getXtenantId(),
      searchString: searchString,
    });

  const [patientSearch, setPatientSearch] = useDebounce<string>("");
  const [patientPagination, setPatientPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: patientList, isFetching: isPatientFetching } =
    useSearchPatientList({
      pagination: patientPagination,
      searchString: patientSearch,
      xTENANTID: getXtenantId(),
    });

  const { mutateAsync: sendPatientForms } = useSendPatientAssessments();
  const { mutateAsync: updateScheduledAssessment } =
    useUpdateScheduledAssessment();

  const handleUpdateScheduledAssessment = (
    v: PatientAssessment,
    assessment: EditAssessmentData
  ) => {
    const screenerType = v.selectedForms?.[0]?.value ?? assessment.screenerType;
    const scheduleTime =
      mergeDateTime(v.recordedDate, v.recordedTime) ?? assessment.scheduleTime;

    showToastOnApiCall({
      apiCall: updateScheduledAssessment({
        assessmentId: assessment.assessmentId,
        xTENANTID: getXtenantId(),
        body: {
          patientId: assessment.patientId,
          appointmentId: assessment.appointmentId ?? undefined,
          screenerType,
          scheduleTime,
        },
      }),
      onSave: () => {
        queryClient.invalidateQueries({
          queryKey: ["patient-chart-assessment-list"],
        });
        onUpdateSuccess?.();
        handleClose();
      },
      showSnackbar: showSnackbar,
    });
  };

  const handleSendPatientForms = (v: any) => {
    if (editAssessment) {
      handleUpdateScheduledAssessment(v, editAssessment);
      return;
    }

    const payload = {
      patientAssessments: {
        ...v,
        reminders: v?.reminder_set ? v?.reminders : undefined,
        assessment: v.selectedForms?.map((v: any) => v.value),
        scheduleTime: mergeDateTime(v.recordedDate, v.recordedTime),
        reminderMedium: ["EMAIL"],
        recordedTime: undefined,
        providerName: providerName ? providerName : "",
        providerRole: providerRole ? providerRole : "",
      },
      patientIds: showPatientSelection
        ? v.patientIds?.map((item: any) => item.value)
        : [selectedPatient?.uuid],
    };

    showToastOnApiCall({
      apiCall: sendPatientForms(payload),
      onSave: () => {
        queryClient.invalidateQueries({
          queryKey: ["patient-chart-assessment-list"],
        });
        handleClose();
      },
      showSnackbar: showSnackbar,
    });
  };

  const editInitialValues = useMemo<Partial<PatientAssessment>>(() => {
    if (!editAssessment) return {};
    const baseDate = editAssessment.scheduleTime
      ? new Date(editAssessment.scheduleTime)
      : undefined;
    return {
      selectedForms: [
        {
          value: editAssessment.screenerType,
          label: replaceUnderScoreAddSpace(editAssessment.screenerType),
        },
      ],
      isScheduled: true,
      recordedDate: baseDate,
      recordedTime: baseDate,
    } as Partial<PatientAssessment>;
  }, [editAssessment]);

  // In edit mode, mark the prefilled date/time as touched so any "past date"
  // validation error is visible immediately, not only after the user
  // interacts with the field.
  const initialTouched = editAssessment
    ? { recordedDate: true, recordedTime: true }
    : undefined;

  const formik = useFormik<PatientAssessment>({
    initialValues: {
      ...SEND_PATIENT_ASSESSMENT_INITIAL_VALUES,
      patientIds: preSelectedPatients,
      ...editInitialValues,
    },
    initialTouched,
    validationSchema: SEND_PATIENT_ASSESSMENT_VALIDATION_SCHEMA,
    onSubmit: handleSendPatientForms,
    enableReinitialize: true,
  });

  const patientOptions = useMemo(() => {
    const fetched =
      patientList?.data?.content?.map((item: any) => ({
        value: item.uuid,
        label: `${item.firstName} ${item.lastName}`,
      })) ?? [];
    const selected = (formik.values.patientIds ?? []) as {
      [k: string]: string;
    }[];
    const missing = selected.filter(
      (s) => !fetched.some((o) => o.value === s.value)
    );
    return uniqBy(
      [...missing, ...(preSelectedPatients ?? []), ...fetched],
      "value"
    );
  }, [patientList?.data?.content, preSelectedPatients, formik.values.patientIds]);

  const screenerOptions = useMemo(() => {
    const fetched =
      screenerTypeList?.data?.content?.map((item: any) => ({
        value: item,
        label: replaceUnderScoreAddSpace(item),
      })) ?? [];
    const selected = (formik.values.selectedForms ?? []) as {
      [k: string]: string;
    }[];
    const missing = selected.filter(
      (s) => !fetched.some((o) => o.value === s.value)
    );
    return [...missing, ...fetched];
  }, [screenerTypeList?.data?.content, formik.values.selectedForms]);

  function handleClose() {
    setSearchString("");
    formik.resetForm();
    disclosureHost.close();
  }

  const mergeDateTime = (date?: Date, time?: Date) => {
    if (!date || !time) return;

    const preDate = new Date(date);
    preDate?.setHours(time.getHours(), time.getMinutes(), time.getSeconds());
    return preDate?.toISOString();
  };

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={handleClose}
      dialogTitle={
        isEdit ? "Edit Scheduled Assessment" : "Send Patient Assessments"
      }
      dialogAction={
        <Flex justifyContent="flex-end" gap={2}>
          <StyledButtonV2
            label="Cancel"
            variant="outlined"
            onClick={handleClose}
          />
          <StyledButtonV2
            label={isEdit ? "Update" : "Send"}
            onClick={() => formik.submitForm()}
            variant="contained"
          />
        </Flex>
      }
    >
      <Flex flexDirection={"column"} gap={2} width={"500px"} pt={2}>
        <Grid item container spacing={2}>
          {showPatientSelection && !isEdit && (
            <Grid item xs={12}>
              <MaskMultiSelectScroll
                optionWithSelectAll
                limitTags={3}
                onSearch={(value) => setPatientSearch(value)}
                placeholder="Select Patients"
                value={formik.values.patientIds}
                onChange={(values) => {
                  formik.setFieldValue("patientIds", values);
                }}
                options={patientOptions}
                error={formik.touched.patientIds && formik.errors.patientIds}
                pagination={patientPagination}
                setPagination={setPatientPagination}
                totalElements={patientList?.data?.page?.totalElements}
                loading={isPatientFetching}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <MaskMultiSelectScroll
              placeholder="Select Screening Forms"
              onSearch={(value) => setSearchString(value)}
              options={screenerOptions}
              value={formik.values.selectedForms}
              onChange={(values) => {
                if (isEdit) {
                  formik.setFieldValue(
                    "selectedForms",
                    values?.slice(-1) ?? []
                  );
                  return;
                }
                formik.setFieldValue("selectedForms", values);
              }}
              limitTags={1}
              error={
                formik.touched.selectedForms && formik.errors.selectedForms
              }
              pagination={screenerPagination}
              setPagination={setScreenerPagination}
              totalElements={screenerTypeList?.data?.page?.totalElements}
              loading={isScreenerFetching}
            />
          </Grid>

          {!isEdit && (
            <Grid item sm={12} md={6} lg={12}>
              <FormControlLabel
                name="isScheduled"
                control={<Checkbox />}
                label={<Text fontWeight={500}>Schedule Assessment</Text>}
                checked={formik.values.isScheduled}
                onChange={(_, checked) => {
                  formik.setFieldValue(
                    "isScheduled",
                    !formik.values.isScheduled
                  );

                  if (checked) {
                    formik.setFieldValue("recordedDate", undefined);
                    formik.setFieldTouched("recordedDate", false);
                    formik.setFieldValue("recordedTime", undefined);
                    formik.setFieldTouched("recordedDate", false);
                  }
                }}
              />
            </Grid>
          )}
          {formik.values.isScheduled && (
            <Grid item xs={12}>
              <Flex gap={2}>
                <CustomDatePicker
                  required
                  label="Date"
                  name="recordedDate"
                  minDate={dayjs()}
                  handleBlur={formik.handleBlur}
                  value={formik.values?.recordedDate}
                  onChange={(val) =>
                    formik.setFieldValue("recordedDate", val?.toISOString())
                  }
                  error={
                    !!formik.touched?.recordedDate &&
                    formik.errors?.recordedDate
                  }
                />

                <CustomTimePicker
                  required
                  label="Time"
                  minTime={
                    formik.values?.recordedDate &&
                    dayjs(formik.values?.recordedDate).isSame(dayjs(), "day")
                      ? dayjs()
                      : undefined
                  }
                  name="recordedTime"
                  handleBlur={formik.handleBlur}
                  value={formik.values?.recordedTime}
                  onChange={(val) => {
                    formik.setFieldValue("recordedTime", dayjs(val)?.toDate());
                  }}
                  error={
                    !!formik.touched?.recordedTime &&
                    formik.errors?.recordedTime
                  }
                />
              </Flex>
            </Grid>
          )}

          {/* Rendering Provider */}

          {formik.values.isScheduled && !isEdit && (
            <Grid item xs={12}>
              <CustomSelect
                readOnly={true}
                name="reminderMedium"
                label="Medium"
                value={"EMAIL"}
                options={MEDIUM_LIST.map((item) => ({
                  value: item.value,
                  label: item.label,
                }))}
                onChange={(val) => formik.setFieldValue("reminderMedium", val)}
                handleBlur={formik.handleBlur}
                error={
                  formik?.touched?.reminderMedium &&
                  formik.errors?.reminderMedium
                }
              />
            </Grid>
          )}

          {formik.values.isScheduled && !isEdit && (
            <Grid
              item
              sm={12}
              md={6}
              container
              rowSpacing={1}
              lg={6}
              alignItems={"start"}
            >
              <FormControlLabel
                name="isRecurring"
                control={<Checkbox />}
                label={<Text fontWeight={500}> Recurring</Text>}
                checked={formik.values.isRecurring}
                onChange={formik.handleChange}
              />

              {formik.values.isRecurring && (
                <RecurringConfig formik={formik as any} />
              )}
            </Grid>
          )}

          {formik.values.isScheduled && !isEdit && (
            <Grid item sm={12} md={6} lg={6}>
              <FormControlLabel
                name="reminder_set"
                control={<Checkbox />}
                label={<Text fontWeight={500}>Set Reminder</Text>}
                checked={formik.values.reminder_set}
                onChange={() => {
                  formik.setFieldValue(
                    "reminder_set",
                    !formik.values.reminder_set
                  );
                  formik.setFieldValue("reminders", undefined);
                }}
              />

              <Grid item xs={12}>
                <SetReminder formik={formik} />
              </Grid>
            </Grid>
          )}
        </Grid>
      </Flex>
    </StyledFormModal>
  );
};

export default SendPatientAssessmentFormsModal;

interface SetReminderProps {
  formik: FormikProps<any>;
}

const SetReminder = ({ formik }: SetReminderProps) => {
  return (
    formik.values.reminder_set && (
      <Grid item container xs={12} spacing={2}>
        <Grid item sm={12}>
          <MultiSelect
            label="Reminder Mediums"
            options={REMINDER_MEDIUMS}
            value={["EMAIL"]}
            onChange={(values) => {
              formik.setFieldValue("reminders.reminderMedium", values);
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <NumberInput
            min={0}
            textFieldProps={{
              value: formik.values.reminders?.reminderBeforeNumber,
              onChange: (e) => {
                formik.setFieldValue(
                  "reminders.reminderBeforeNumber",
                  e.currentTarget.value
                );
              },
            }}
            label="Remind Before"
          />
        </Grid>
        <Grid item xs={8}>
          <CustomSelect
            label=""
            options={REMINDER_BEFORE_UNITS}
            clearable={false}
            value={formik.values.reminders?.reminderBeforeUnit}
            onChange={(value) => {
              formik.setFieldValue("reminders.reminderBeforeUnit", value);
            }}
          />
        </Grid>
      </Grid>
    )
  );
};
